/**
 * Network Error Suppressor
 * Wraps native fetch to suppress browser console errors from failed network requests
 */

const isProduction = () => {
  return (
    process.env.NODE_ENV === 'production' ||
    process.env.REACT_APP_ENV === 'production' ||
    (typeof window !== 'undefined' && 
     window.location.hostname !== 'localhost' && 
     !window.location.hostname.includes('127.0.0.1') &&
     !window.location.hostname.includes('dev') &&
     !window.location.hostname.includes('staging'))
  );
};

/**
 * Wrap native fetch to suppress console errors
 */
export const suppressNetworkErrors = () => {
  if (!isProduction()) {
    return; // Don't suppress in development
  }

  // Store original fetch
  const originalFetch = window.fetch;

  // Override fetch to catch and suppress errors
  window.fetch = async (...args) => {
    try {
      const response = await originalFetch(...args);
      return response;
    } catch (error) {
      // Suppress the error completely - return a fake failed response
      // This prevents "Failed to load resource" errors in console
      return new Response(JSON.stringify({ error: 'Network error' }), {
        status: 500,
        statusText: 'Network Error',
        headers: { 'Content-Type': 'application/json' }
      });
    }
  };

  // Store original XMLHttpRequest
  const OriginalXHR = window.XMLHttpRequest;

  // Override XMLHttpRequest to suppress errors
  window.XMLHttpRequest = function() {
    const xhr = new OriginalXHR();
    
    // Wrap onerror to suppress
    const originalOnError = xhr.onerror;
    xhr.onerror = function(...args) {
      // Silently handle error
      if (typeof originalOnError === 'function') {
        originalOnError.apply(this, args);
      }
    };

    return xhr;
  };

  // Inherit prototype
  window.XMLHttpRequest.prototype = OriginalXHR.prototype;

  // Suppress unhandled promise rejections from network errors
  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason;
    
    // Check if it's a network-related error
    if (
      error &&
      (error.message?.includes('Failed to fetch') ||
       error.message?.includes('NetworkError') ||
       error.message?.includes('CORS') ||
       error.message?.includes('ERR_FAILED') ||
       error.name === 'TypeError' ||
       error.name === 'NetworkError')
    ) {
      // Suppress the error
      event.preventDefault();
    }
  });

  // Suppress general errors from network failures
  window.addEventListener('error', (event) => {
    const error = event.error;
    const message = event.message || '';
    
    // Check if it's a network-related error
    if (
      message.includes('Failed to fetch') ||
      message.includes('NetworkError') ||
      message.includes('CORS') ||
      message.includes('ERR_FAILED') ||
      message.includes('Failed to load resource') ||
      error?.message?.includes('Failed to fetch') ||
      error?.message?.includes('NetworkError')
    ) {
      // Suppress the error
      event.preventDefault();
    }
  });
};

/**
 * Initialize all error suppression
 */
export const initializeErrorSuppression = () => {
  if (!isProduction()) {
    return;
  }

  suppressNetworkErrors();

  // Additional: Suppress specific console methods in production
  const noop = () => {};
  
  // Override console.error to filter out network errors
  const originalConsoleError = console.error;
  console.error = function(...args) {
    // In production, check if it's a network error and suppress
    const errorString = args.join(' ').toLowerCase();
    if (
      errorString.includes('failed to load resource') ||
      errorString.includes('failed to fetch') ||
      errorString.includes('networkerror') ||
      errorString.includes('cors') ||
      errorString.includes('err_failed') ||
      errorString.includes('403') ||
      errorString.includes('404') ||
      errorString.includes('400') ||
      errorString.includes('500')
    ) {
      // Suppress this error
      return;
    }
    // Allow other errors through (but they should be suppressed by consoleSuppress.js)
    // originalConsoleError.apply(console, args);
  };

  // Override console.warn to filter out network warnings
  const originalConsoleWarn = console.warn;
  console.warn = function(...args) {
    // In production, check if it's a network warning and suppress
    const warnString = args.join(' ').toLowerCase();
    if (
      warnString.includes('failed to load') ||
      warnString.includes('cors') ||
      warnString.includes('network')
    ) {
      // Suppress this warning
      return;
    }
    // Allow other warnings through (but they should be suppressed by consoleSuppress.js)
    // originalConsoleWarn.apply(console, args);
  };
};

export default {
  suppress: suppressNetworkErrors,
  initialize: initializeErrorSuppression,
  isProduction
};
